import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import SettingsIcon from '@mui/icons-material/Settings';
import ShareIcon from '@mui/icons-material/Share';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import MarkInsertCollapsibleCard from '@/components/review/MarkInsertCollapsibleCard';
import api from '@/services/api';
import PageHeader from '@/components/Layout/PageHeader';
import YouTubePlayer, { type YouTubePlayerHandle } from '@/components/YouTubePlayer';
import SessionFormDialog, { type SessionFormValues } from '@/components/SessionFormDialog';
import RefereeEventTimeline from '@/components/review/RefereeEventTimeline';
import ReviewVideoPlayerShell from '@/components/review/ReviewVideoPlayerShell';
import DecisionFields, { emptyDecisionForm, isDecisionMarkReady, normalizeDecisionPayload, type DecisionFormValues } from '@/components/review/DecisionFields';
import ShareEditDialog from '@/components/review/ShareEditDialog';
import ShareSessionDialog from '@/components/review/ShareSessionDialog';
import ReviewAnalyticsPanel from '@/components/review/ReviewAnalyticsPanel';
import ReviewProMode from '@/components/review/ReviewProMode';
import DecisionFilterPanel from '@/components/review/DecisionFilterPanel';
import ReviewViewModeToolbar from '@/components/review/ReviewViewModeToolbar';
import OfficialSourceInline from '@/components/review/OfficialSourceInline';
import ConsensusRateInfoIcon from '@/components/review/ConsensusRateInfoIcon';
import {
  isUserOfficialSource,
  resolveOfficialSourceDisplayNames,
  resolveOfficialUserId,
  resolveOfficialUserIds,
} from '@/components/review/officialSource';
import { buildOfficialSourceMemberOptions } from '@/utils/groupMembers';
import {
  hasReviewSessionSetupInfo,
  ReviewSessionSetupLine,
} from '@/components/review/ReviewSessionListItems';
import {
  buildDecisionFilterOptions,
  clusterMatchesFilters,
  eventMatchesFilters,
  type ActiveDecisionFilter,
} from '@/components/review/decisionFilters';
import {
  buildConsensusClusters,
  buildClusterTimelineEvents,
  getTimelineMarkerIcon,
  resolveMarkSelectionId,
} from '@/components/review/consensus';
import { useAuth } from '@/hooks/useAuth';
import { usePageDocumentTitle } from '@/contexts/PageDocumentTitleContext';
import {
  DEFAULT_AWAY_COLOR,
  DEFAULT_HOME_COLOR,
  canEditAllSessionEvents,
  getSessionRoleLabel,
  roleFromSessionAccess,
  type ConsolidatedEventCluster,
  type EventResponse,
  type ReviewSession,
  type SessionEvent,
  type SessionShare,
  formatVideoTime,
  PARTICIPANT_DEADLINE_MESSAGE,
} from '@/types';

type PanelTab = 'review' | 'analytics';

export default function ReviewDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const groupId = searchParams.get('group_id') || undefined;
  const initialTab = searchParams.get('tab') === 'analytics' ? 'analytics' as const : 'review' as const;
  const { user } = useAuth();
  const playerRef = useRef<YouTubePlayerHandle>(null);

  const [session, setSession] = useState<ReviewSession | null>(null);
  const [events, setEvents] = useState<SessionEvent[]>([]);
  const [responses, setResponses] = useState<EventResponse[]>([]);
  const [shares, setShares] = useState<SessionShare[]>([]);
  const [groupName, setGroupName] = useState<string | null>(null);
  const [groupMembers, setGroupMembers] = useState<
    Array<{ user_id: number; full_name?: string | null; reference_no: string; role: string }>
  >([]);
  const [decisionFilters, setDecisionFilters] = useState<ActiveDecisionFilter[]>([]);
  const [currentTime, setCurrentTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [tab, setTab] = useState<PanelTab>(initialTab);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [proModeOpen, setProModeOpen] = useState(false);
  const [educationMode, setEducationMode] = useState(false);
  const [markInsertCollapsed, setMarkInsertCollapsed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  usePageDocumentTitle(session?.title);

  const [markForm, setMarkForm] = useState<DecisionFormValues>(emptyDecisionForm());

  const load = useCallback(async () => {
    if (!id) return null;
    const params = groupId ? { group_id: groupId } : undefined;
    const res = await api.get<{
      session: ReviewSession;
      events: SessionEvent[];
      responses: EventResponse[];
      shares: SessionShare[];
    }>(`/review-sessions/${id}`, { params });
    setSession(res.data.session);
    setEvents(res.data.events);
    setResponses(res.data.responses);
    setShares(res.data.shares);
    setError(null);
    return res.data;
  }, [id, groupId]);

  useEffect(() => {
    if (!groupId) {
      setGroupName(null);
      setGroupMembers([]);
      return;
    }
    api
      .get<{
        group: { name: string };
        members: Array<{
          user_id: number;
          full_name?: string | null;
          reference_no: string;
          role: string;
        }>;
      }>(`/groups/${groupId}`)
      .then((r) => {
        setGroupName(r.data.group?.name ?? null);
        setGroupMembers(r.data.members ?? []);
      })
      .catch(() => {
        setGroupName(null);
        setGroupMembers([]);
      });
  }, [groupId]);

  useEffect(() => {
    load().catch(() => setError('無法載入場次'));
  }, [load]);

  useEffect(() => {
    setVideoDuration(0);
  }, [session?.youtube_video_id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (proModeOpen) return;
      if (e.code === 'Space' && !['INPUT', 'TEXTAREA', 'SELECT'].includes((e.target as HTMLElement)?.tagName)) {
        e.preventDefault();
        playerRef.current?.togglePlay();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [proModeOpen]);

  const access = session?.access;
  const sessionRole = roleFromSessionAccess(access);
  const isViewerOnly = sessionRole === 'viewer';
  const isParticipantOnly = sessionRole === 'participant';
  const participantDeadlinePassed = !!access?.participant_deadline_passed;
  const canEditAllEvents = canEditAllSessionEvents(access);
  const canViewOfficialMarks = !!access?.can_view_official_marks;
  const isComparisonMode = isParticipantOnly && canViewOfficialMarks;
  /** Shared cohort marking (參與者 + 管理員); 建立者 uses official mark card instead. */
  const usesSharedParticipantMarking = !!(access?.is_participant && !access?.can_mark_official);
  const [consensusTimeline, setConsensusTimeline] = useState(false);
  const [integrateOfficialMarks, setIntegrateOfficialMarks] = useState(false);

  const groupContextPayload = useMemo(() => {
    if (groupId) return { group_id: groupId };
    if (access?.group_id) return { group_id: access.group_id };
    if (access?.share_id) return { share_id: access.share_id };
    return {};
  }, [groupId, access?.group_id, access?.share_id]);

  const groupShare = useMemo(
    () =>
      groupId
        ? shares.find((s) => s.share_type === 'group' && s.target_group_id === groupId) ?? null
        : null,
    [shares, groupId]
  );
  const canViewAnalytics = !!(access?.can_view_analytics || access?.can_view_share_analytics);
  const canUseConsensusTimeline =
    !isViewerOnly && !!(canViewAnalytics || access?.can_mark_official || canEditAllEvents);
  const showConsensusGrouping = consensusTimeline && canUseConsensusTimeline;
  /** Facilitators (建立者、群組管理員、developer/admin) share one consensus cluster view with % 共識. */
  const showFacilitatorConsensusTimeline =
    showConsensusGrouping && !isViewerOnly && !isComparisonMode;
  /** Owner, admin/developer, and group managers may edit any row in integrated consensus timeline. */
  const canEditConsensusTimelineMarks =
    canEditAllEvents || !!access?.can_mark_official || canViewAnalytics;
  const consensusTimelineReadOnly = showConsensusGrouping && !canEditConsensusTimelineMarks;
  const canOpenEventStats = !!(canEditAllEvents || access?.can_mark_official || canViewAnalytics);
  const canViewComparison = !isViewerOnly && (isComparisonMode || canViewAnalytics);
  const canManageEducationMode = !!(
    access?.is_owner ||
    access?.can_mark_official ||
    access?.can_release_official ||
    canEditAllEvents
  );
  const showViewModeToggles =
    canUseConsensusTimeline || canManageEducationMode || isComparisonMode;
  const showParticipantOfficialIntegration = isComparisonMode && integrateOfficialMarks;
  const showTimelineConsensusView = showConsensusGrouping || showParticipantOfficialIntegration;
  const homeColor = session?.home_team_color || DEFAULT_HOME_COLOR;
  const awayColor = session?.away_team_color || DEFAULT_AWAY_COLOR;
  useEffect(() => {
    setDecisionFilters([]);
  }, [id, groupId]);

  useEffect(() => {
    if (!consensusTimeline) setEducationMode(false);
  }, [consensusTimeline]);

  useEffect(() => {
    if (!isComparisonMode) setIntegrateOfficialMarks(false);
  }, [isComparisonMode]);

  const autoCollapseOfficialMarkOnEducation = educationMode && !!access?.can_mark_official;

  useEffect(() => {
    if (autoCollapseOfficialMarkOnEducation) {
      setMarkInsertCollapsed(true);
    } else if (!educationMode || !consensusTimeline) {
      setMarkInsertCollapsed(false);
    }
  }, [autoCollapseOfficialMarkOnEducation, educationMode, consensusTimeline]);

  const officialUserIds = useMemo(
    () => resolveOfficialUserIds(access, session?.created_by_user_id),
    [access, session?.created_by_user_id]
  );
  const officialUserId = officialUserIds[0];
  const officialSourceOptions = useMemo(
    () =>
      buildOfficialSourceMemberOptions(
        groupMembers,
        session?.created_by_user_id,
        (m) => m.full_name?.trim() || m.reference_no,
        user?.id
      ),
    [groupMembers, session?.created_by_user_id, user?.id]
  );
  const officialMemberLabels = useMemo(
    () =>
      groupMembers.map((m) => ({
        user_id: m.user_id,
        label: m.full_name?.trim() || m.reference_no,
      })),
    [groupMembers]
  );
  const resolveOfficialLabel = useCallback(
    (userId: number) =>
      resolveOfficialSourceDisplayNames([userId], {
        currentUserId: user?.id,
        sessionOwnerId: session?.created_by_user_id,
        members: officialMemberLabels,
      }),
    [user?.id, session?.created_by_user_id, officialMemberLabels]
  );
  const isOfficialSourceUser = isUserOfficialSource(user?.id, officialUserIds);
  const officialSourceDisplayName = useMemo(
    () =>
      resolveOfficialSourceDisplayNames(officialUserIds, {
        currentUserId: user?.id,
        sessionOwnerId: session?.created_by_user_id,
        members: officialMemberLabels,
      }),
    [officialUserIds, user?.id, session?.created_by_user_id, officialMemberLabels]
  );
  const timelineScopeSuffix = showFacilitatorConsensusTimeline
    ? '（整合全體回覆）'
    : showParticipantOfficialIntegration
      ? '（整合官方判決）'
      : !showTimelineConsensusView && !isViewerOnly
        ? '（我的）'
        : '';
  const showOfficialSourceNotice =
    !isViewerOnly &&
    officialUserId != null &&
    !!(canViewAnalytics || canEditAllEvents || access?.can_mark_official);
  const showOfficialMarkCard =
    !isViewerOnly &&
    isOfficialSourceUser &&
    !!(access?.can_mark_official || (access?.can_mark_participant && !participantDeadlinePassed));
  const showParticipantMarkCard =
    !isViewerOnly &&
    !isOfficialSourceUser &&
    !!(access?.can_mark_participant && !participantDeadlinePassed);
  const clusters = useMemo<ConsolidatedEventCluster[]>(
    () => buildConsensusClusters(events, responses, { ownerUserIds: officialUserIds }),
    [events, responses, officialUserIds]
  );

  const isMyMark = useCallback(
    (e: SessionEvent) =>
      user?.id != null &&
      Number(e.created_by_user_id) === Number(user.id) &&
      !!(e.on_field_decision || e.judgement || e.note?.trim()),
    [user?.id]
  );

  const isOfficialMark = useCallback(
    (e: SessionEvent) =>
      officialUserIds.some((id) => Number(e.created_by_user_id) === Number(id)) &&
      !!(e.on_field_decision || e.judgement || e.note?.trim()),
    [officialUserIds]
  );

  const participantIntegrationEvents = useMemo(() => {
    if (!isComparisonMode) return [];
    return events.filter((e) => {
      if (!e.on_field_decision && !e.judgement && !e.note?.trim()) return false;
      return isMyMark(e) || isOfficialMark(e);
    });
  }, [events, isComparisonMode, isMyMark, isOfficialMark]);

  const participantIntegrationClusters = useMemo(
    () =>
      buildConsensusClusters(participantIntegrationEvents, responses, {
        ownerUserIds: officialUserIds,
      }),
    [participantIntegrationEvents, responses, officialUserIds]
  );

  const eventsForFilterOptions = useMemo(() => {
    return events.filter((e) => {
      if (!e.on_field_decision && !e.judgement && !e.note?.trim()) return false;
      if (isViewerOnly) {
        return e.marker_role !== 'participant';
      }
      if (showFacilitatorConsensusTimeline) {
        return true;
      }
      if (showParticipantOfficialIntegration) {
        return isMyMark(e) || isOfficialMark(e);
      }
      if (showConsensusGrouping) {
        return e.marker_role !== 'participant' || isMyMark(e);
      }
      return isMyMark(e);
    });
  }, [
    events,
    isViewerOnly,
    showConsensusGrouping,
    showFacilitatorConsensusTimeline,
    showParticipantOfficialIntegration,
    isMyMark,
    isOfficialMark,
  ]);

  const filterOptions = useMemo(
    () => buildDecisionFilterOptions(eventsForFilterOptions),
    [eventsForFilterOptions]
  );

  const eventMap = useMemo(() => new Map(events.map((e) => [e.id, e])), [events]);

  const filteredClusters = useMemo(() => {
    if (decisionFilters.length === 0) return clusters;
    return clusters.filter((c) => clusterMatchesFilters(c, eventMap, decisionFilters));
  }, [clusters, decisionFilters, eventMap]);

  const filteredParticipantIntegrationClusters = useMemo(() => {
    if (decisionFilters.length === 0) return participantIntegrationClusters;
    return participantIntegrationClusters.filter((c) =>
      clusterMatchesFilters(c, eventMap, decisionFilters)
    );
  }, [participantIntegrationClusters, decisionFilters, eventMap]);

  const filteredEvents = useMemo(() => {
    if (decisionFilters.length === 0) return events;
    return events.filter((e) => eventMatchesFilters(e, decisionFilters));
  }, [events, decisionFilters]);

  const timelineEvents = useMemo(() => {
    const activeClusters = showParticipantOfficialIntegration
      ? decisionFilters.length > 0
        ? filteredParticipantIntegrationClusters
        : participantIntegrationClusters
      : decisionFilters.length > 0
        ? filteredClusters
        : clusters;
    const source = decisionFilters.length > 0 ? filteredEvents : events;
    if (isViewerOnly) {
      return source
        .filter((e) => e.marker_role !== 'participant' && e.video_second != null)
        .sort((a, b) => (a.video_second ?? 0) - (b.video_second ?? 0));
    }
    if (showTimelineConsensusView && activeClusters.length > 0) {
      return buildClusterTimelineEvents(activeClusters, officialUserId);
    }
    return source
      .filter((e) => isMyMark(e) && e.video_second != null)
      .sort((a, b) => (a.video_second ?? 0) - (b.video_second ?? 0));
  }, [
    clusters,
    filteredClusters,
    participantIntegrationClusters,
    filteredParticipantIntegrationClusters,
    filteredEvents,
    events,
    showTimelineConsensusView,
    showParticipantOfficialIntegration,
    isViewerOnly,
    isMyMark,
    decisionFilters.length,
    officialUserId,
  ]);

  const displayClusters = useMemo(() => {
    if (isViewerOnly) return [];
    const base = showParticipantOfficialIntegration
      ? participantIntegrationClusters
      : clusters;
    if (decisionFilters.length === 0) return base;
    return showParticipantOfficialIntegration
      ? filteredParticipantIntegrationClusters
      : filteredClusters;
  }, [
    isViewerOnly,
    showParticipantOfficialIntegration,
    participantIntegrationClusters,
    clusters,
    decisionFilters.length,
    filteredParticipantIntegrationClusters,
    filteredClusters,
  ]);

  const timelineClusterById = useMemo(
    () => new Map(displayClusters.map((c) => [c.clusterId, c])),
    [displayClusters]
  );

  const resolveMarkerIcon = useCallback(
    (event: SessionEvent) =>
      getTimelineMarkerIcon(event, {
        clusterById: showTimelineConsensusView ? timelineClusterById : undefined,
        officialUserId,
      }),
    [showTimelineConsensusView, timelineClusterById, officialUserId]
  );

  const displayEvents = useMemo(() => {
    if (isViewerOnly) {
      const base = decisionFilters.length > 0 ? filteredEvents : events;
      return base.filter((e) => e.marker_role !== 'participant');
    }
    if (showParticipantOfficialIntegration) {
      const src = participantIntegrationEvents;
      if (decisionFilters.length === 0) return src;
      return src.filter((e) => eventMatchesFilters(e, decisionFilters));
    }
    const base = decisionFilters.length > 0 ? filteredEvents : events;
    if (showConsensusGrouping) {
      return base;
    }
    return base.filter((e) => isMyMark(e));
  }, [
    decisionFilters,
    filteredEvents,
    events,
    isViewerOnly,
    showConsensusGrouping,
    showParticipantOfficialIntegration,
    participantIntegrationEvents,
    isMyMark,
  ]);

  const proModeEvents = useMemo(() => {
    if (isViewerOnly) return displayEvents;
    if (showParticipantOfficialIntegration) {
      if (decisionFilters.length === 0) return participantIntegrationEvents;
      return participantIntegrationEvents.filter((e) =>
        eventMatchesFilters(e, decisionFilters)
      );
    }
    return events;
  }, [
    isViewerOnly,
    displayEvents,
    showParticipantOfficialIntegration,
    participantIntegrationEvents,
    decisionFilters,
    events,
  ]);

  const addOfficialMark = async (payload?: ReturnType<typeof normalizeDecisionPayload>, videoSecond?: number) => {
    if (!id || !access?.can_mark_official) return;
    const body = payload || normalizeDecisionPayload(markForm);
    const t = videoSecond ?? playerRef.current?.getCurrentTime() ?? currentTime;
    const res = await api.post<{ event: SessionEvent }>('/events', {
      session_id: id,
      session_type: 'review',
      marker_role: 'official',
      video_second: t,
      ...body,
    });
    if (!payload) setMarkForm(emptyDecisionForm());
    const data = await load();
    if (!data) return;
    const selectId = resolveMarkSelectionId(res.data.event, data.events, data.responses, {
      useConsensusSelection: showTimelineConsensusView,
      officialUserId,
    });
    setSelectedEventId(selectId);
    return selectId;
  };

  const addParticipantMark = async (payload?: ReturnType<typeof normalizeDecisionPayload>, videoSecond?: number) => {
    if (!id || !access?.can_mark_participant) return;
    const body = payload || normalizeDecisionPayload(markForm);
    const t = videoSecond ?? playerRef.current?.getCurrentTime() ?? currentTime;
    try {
      const res = await api.post<{ event: SessionEvent }>('/events', {
        session_id: id,
        session_type: 'review',
        marker_role: 'participant',
        video_second: t,
        ...groupContextPayload,
        ...body,
      });
      if (!payload) setMarkForm(emptyDecisionForm());
      const data = await load();
      if (!data) return;
      const selectId = resolveMarkSelectionId(res.data.event, data.events, data.responses, {
        useConsensusSelection: showTimelineConsensusView,
        officialUserId,
      });
      setSelectedEventId(selectId);
      return selectId;
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        '無法新增標記';
      setError(msg);
      throw err;
    }
  };

  const submitResponse = async (officialEventId: string, payload: ReturnType<typeof normalizeDecisionPayload>) => {
    if (!id) return;
    await api.post('/responses', {
      session_id: id,
      official_event_id: officialEventId,
      ...groupContextPayload,
      ...payload,
    });
    await load();
  };

  const updateEvent = async (eventId: string, payload: ReturnType<typeof normalizeDecisionPayload> & { video_second?: number }) => {
    try {
      await api.put(`/events/${eventId}`, { ...payload, ...groupContextPayload });
      await load();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        '無法更新標記';
      setError(msg);
      throw err;
    }
  };

  const handleUpdateSession = async (values: SessionFormValues) => {
    if (!id) return;
    await api.put(`/review-sessions/${id}`, values);
    setSettingsOpen(false);
    await load();
  };

  const handleDeleteEvent = async (event: SessionEvent) => {
    if (!confirm('刪除此標記？')) return;
    await api.delete(`/events/${event.id}`, { params: groupContextPayload });
    await load();
  };

  const handleDeleteEventById = async (eventId: string) => {
    const event = events.find((e) => e.id === eventId);
    if (event) await handleDeleteEvent(event);
  };

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!session) return <Typography>載入中…</Typography>;

  return (
    <Box>
      {error && session && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <PageHeader
        backTo={groupId ? `/groups/${groupId}` : '/review'}
        backLabel={groupId ? '返回群組' : '影片分析'}
        icon={<PlayCircleOutlineIcon />}
        title={session.title}
        titleVariant="h5"
        subtitle={hasReviewSessionSetupInfo(session) ? <ReviewSessionSetupLine session={session} /> : undefined}
        chips={
          <>
            <Chip
              size="small"
              label={getSessionRoleLabel(roleFromSessionAccess(access))}
              color={
                access?.is_owner
                  ? 'secondary'
                  : roleFromSessionAccess(access) === 'manager'
                    ? 'warning'
                    : access?.is_participant
                      ? 'info'
                      : 'default'
              }
            />
            {session.is_shared && <Chip size="small" color="info" label="已分享給我" />}
            {groupId && groupName && (
              <Chip size="small" variant="outlined" color="secondary" label={`群組：${groupName}`} />
            )}
            {isComparisonMode && (
              <Chip size="small" color="success" variant="outlined" label="已發佈官方判決" />
            )}
          </>
        }
        chipRowTrailing={
          showOfficialSourceNotice ? (
            <OfficialSourceInline displayName={officialSourceDisplayName} />
          ) : undefined
        }
        actions={
          <>
            <Button size="small" startIcon={<FullscreenIcon />} onClick={() => setProModeOpen(true)}>
              {isViewerOnly
                ? '全螢幕'
                : usesSharedParticipantMarking || isParticipantOnly
                  ? '全螢幕標記'
                  : '全螢幕分析'}
            </Button>
            <Button size="small" startIcon={<DownloadIcon />} href={`/api/review-sessions/${id}/export?format=json`} target="_blank">
              JSON
            </Button>
            <Button
              size="small"
              startIcon={<DownloadIcon />}
              href={`/api/review-sessions/${id}/export?format=xls${groupId ? `&group_id=${encodeURIComponent(groupId)}` : ''}`}
              target="_blank"
            >
              Excel
            </Button>
            {access?.can_share && (
              <Button size="small" startIcon={<ShareIcon />} onClick={() => setShareOpen(true)}>
                {groupId ? '分享設定' : '分享'}
              </Button>
            )}
            {access?.can_edit && (
              <Button size="small" startIcon={<SettingsIcon />} onClick={() => setSettingsOpen(true)}>
                設定
              </Button>
            )}
          </>
        }
        mb={2}
      />

      {(canViewComparison || showViewModeToggles) && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          spacing={{ xs: 1, sm: 1.5 }}
          sx={{
            mb: 2,
            pb: 0.5,
            borderBottom: 1,
            borderColor: 'divider',
            gap: 1,
          }}
        >
          {canViewComparison ? (
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              sx={{
                minHeight: 44,
                flex: '1 1 auto',
                minWidth: 0,
                '& .MuiTab-root': { minHeight: 44, textTransform: 'none', fontWeight: 600, fontSize: '0.9375rem' },
                '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' },
              }}
            >
              <Tab value="review" label="影片分析" />
              <Tab
                value="analytics"
                label={
                  isComparisonMode
                    ? '判決比對'
                    : access?.can_view_share_analytics && !access?.can_view_analytics
                      ? '群組回覆'
                      : '評核分析'
                }
              />
            </Tabs>
          ) : (
            <Typography variant="subtitle1" fontWeight={700} sx={{ py: 1.25, px: 0.5 }}>
              影片分析
            </Typography>
          )}
          {tab === 'review' && showViewModeToggles && (
            <ReviewViewModeToolbar
              canUseConsensusTimeline={canUseConsensusTimeline}
              consensusTimeline={consensusTimeline}
              onConsensusTimelineChange={setConsensusTimeline}
              canManageEducationMode={canManageEducationMode}
              educationMode={educationMode}
              onEducationModeChange={setEducationMode}
              canIntegrateOfficialMarks={isComparisonMode}
              integrateOfficialMarks={integrateOfficialMarks}
              onIntegrateOfficialMarksChange={setIntegrateOfficialMarks}
            />
          )}
        </Stack>
      )}

      {tab === 'analytics' ? (
        <ReviewAnalyticsPanel
          clusters={clusters}
          events={events}
          canEditEvents={
            canEditAllEvents || (isComparisonMode && !participantDeadlinePassed)
          }
          participantView={isComparisonMode}
          currentUserId={user?.id}
          officialUserIds={officialUserIds}
          resolveOfficialLabel={resolveOfficialLabel}
          videoDuration={videoDuration}
          videoId={session.youtube_video_id}
          onEditEvent={async (eventId, values) => {
            await updateEvent(eventId, values);
          }}
          onJumpToTime={(seconds) => {
            setTab('review');
            setSelectedEventId(null);
            setCurrentTime(seconds);
            playerRef.current?.seekTo(seconds);
          }}
          onPreviewTime={(seconds) => {
            setCurrentTime(seconds);
            playerRef.current?.seekTo(seconds);
          }}
        />
      ) : (
        <>
          {isComparisonMode && (
            <Alert severity="info" sx={{ mb: 2 }}>
              官方判決已發佈。可開啟「整合官方判決」在判決時間軸對照官方標記（含分組與一致／分歧標示），或於「判決比對」查看詳細比對。
            </Alert>
          )}
          <Box
            sx={{
              display: { xs: 'flex', lg: 'grid' },
              flexDirection: { xs: 'column', lg: 'unset' },
              gridTemplateColumns: { lg: 'minmax(0, 7fr) minmax(0, 5fr)' },
              gap: 2,
              alignItems: { lg: 'stretch' },
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  position: { xs: 'static', lg: 'sticky' },
                  top: { lg: 72 },
                  zIndex: 1,
                }}
              >
              {!proModeOpen && (
                <ReviewVideoPlayerShell
                  playerRef={playerRef}
                  videoId={session.youtube_video_id}
                  playbackRate={playbackRate}
                  initialTime={currentTime}
                  currentTime={currentTime}
                  videoDuration={videoDuration}
                  isPlaying={isPlaying}
                  timelineEvents={timelineEvents}
                  selectedEventId={selectedEventId}
                  educationMode={educationMode}
                  onTimeUpdate={setCurrentTime}
                  onDurationReady={setVideoDuration}
                  onPlayingChange={setIsPlaying}
                  onSelectEvent={(e) => setSelectedEventId(e.id)}
                  onSeek={(s) => playerRef.current?.seekTo(s)}
                  onTogglePlay={() => playerRef.current?.togglePlay()}
                  onPlaybackRateChange={setPlaybackRate}
                  layout="inline"
                  resolveMarkerIcon={resolveMarkerIcon}
                />
              )}
              {proModeOpen && (
                <Box
                  sx={{
                    width: '100%',
                    aspectRatio: '16/9',
                    bgcolor: 'grey.900',
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {isViewerOnly
                      ? '全螢幕模式中…'
                      : usesSharedParticipantMarking
                        ? '全螢幕標記模式中…'
                        : '全螢幕分析模式中…'}
                  </Typography>
                </Box>
              )}
              </Box>
            </Box>

            <Stack spacing={2} sx={{ minWidth: 0 }}>
                {showOfficialMarkCard && (
                  <MarkInsertCollapsibleCard
                    title="新增官方判決標記"
                    collapsed={markInsertCollapsed}
                    onCollapsedChange={setMarkInsertCollapsed}
                    expandDisabled={autoCollapseOfficialMarkOnEducation}
                  >
                    <Stack spacing={1.5}>
                      <DecisionFields
                        values={markForm}
                        onChange={(field, value) => setMarkForm((v) => ({ ...v, [field]: value }))}
                      />
                      <Button
                        variant="contained"
                        color="secondary"
                        fullWidth
                        disabled={!isDecisionMarkReady(markForm)}
                        onClick={() =>
                          access?.can_mark_official ? addOfficialMark() : addParticipantMark()
                        }
                      >
                        於 {formatVideoTime(currentTime)} 標記判決
                      </Button>
                    </Stack>
                  </MarkInsertCollapsibleCard>
                )}

                {showParticipantMarkCard && (
                  <MarkInsertCollapsibleCard
                    title="新增判決標記"
                    collapsed={markInsertCollapsed}
                    onCollapsedChange={setMarkInsertCollapsed}
                  >
                    <Stack spacing={1.5}>
                      <DecisionFields
                        values={markForm}
                        onChange={(field, value) => setMarkForm((v) => ({ ...v, [field]: value }))}
                      />
                      <Button
                        variant="contained"
                        color="secondary"
                        fullWidth
                        disabled={!isDecisionMarkReady(markForm)}
                        onClick={() => addParticipantMark()}
                      >
                        於 {formatVideoTime(currentTime)} 標記判決
                      </Button>
                    </Stack>
                  </MarkInsertCollapsibleCard>
                )}

                <Card>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                        <Stack direction="row" alignItems="center" spacing={0.25} flexWrap="wrap">
                          <Typography variant="subtitle1" fontWeight={700}>
                            判決時間軸{timelineScopeSuffix}
                            {decisionFilters.length > 0 && (
                            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                              ({timelineEvents.length} 項)
                            </Typography>
                          )}
                          </Typography>
                          {showTimelineConsensusView && <ConsensusRateInfoIcon />}
                        </Stack>
                        {showOfficialSourceNotice && showFacilitatorConsensusTimeline && (
                          <OfficialSourceInline displayName={officialSourceDisplayName} dense />
                        )}
                      </Stack>
                      {filterOptions.length > 0 && (
                        <DecisionFilterPanel
                          options={filterOptions}
                          active={decisionFilters}
                          onChange={setDecisionFilters}
                        />
                      )}
                    </Stack>
                    <RefereeEventTimeline
                      events={displayEvents}
                      responses={isViewerOnly ? [] : responses}
                      clusters={displayClusters}
                      homeColor={homeColor}
                      awayColor={awayColor}
                      homeTeam={session.home_team || undefined}
                      awayTeam={session.away_team || undefined}
                      canEditOfficial={!!access?.can_mark_official}
                      canEditAllEvents={canEditAllEvents}
                      canViewEventStats={canOpenEventStats}
                      canEditOwnMarks={usesSharedParticipantMarking || isParticipantOnly}
                      selectedEventId={selectedEventId}
                      onSelect={(e) => setSelectedEventId(e.id)}
                      onSeek={(s) => playerRef.current?.seekTo(s)}
                      onPlay={() => playerRef.current?.play()}
                      onPause={() => playerRef.current?.pause()}
                      onEditSubmit={async (event, values) => updateEvent(event.id, values)}
                      onDelete={handleDeleteEvent}
                      videoDuration={videoDuration}
                      currentUserId={user?.id}
                      officialSourceUserId={officialUserId}
                      officialSourceUserIds={officialUserIds}
                      timelineScopeSuffix={timelineScopeSuffix}
                      showConsensusView={showTimelineConsensusView}
                      consensusViewReadOnly={consensusTimelineReadOnly}
                      showParticipantMarkers={
                        !isViewerOnly && showTimelineConsensusView && !showFacilitatorConsensusTimeline
                      }
                      participantComparisonView={isComparisonMode}
                      officialVsAllView={false}
                      ownParticipantMarksOnly={
                        !showConsensusGrouping && !showParticipantOfficialIntegration
                      }
                      hideTitle
                      educationMode={educationMode}
                      participantDeadlinePassed={participantDeadlinePassed}
                      canRespond={!!access?.can_mark_participant && !participantDeadlinePassed}
                    />
                  </CardContent>
                </Card>
            </Stack>
          </Box>
        </>
      )}

      <SessionFormDialog
        open={settingsOpen}
        title="編輯場次設定"
        showYoutube
        initial={{
          title: session.title,
          competition_name: session.competition_name || '',
          home_team: session.home_team || '',
          away_team: session.away_team || '',
          match_date: session.match_date || '',
          youtube_url: session.youtube_url || '',
          notes: session.notes || '',
          home_team_color: homeColor,
          away_team_color: awayColor,
        }}
        onClose={() => setSettingsOpen(false)}
        onSubmit={handleUpdateSession}
      />

      {groupId ? (
        <ShareEditDialog
          open={shareOpen}
          share={groupShare}
          targetLabel={
            groupShare
              ? `${groupName || '群組'} · ${session.title}`
              : groupName
                ? `${groupName} · ${session.title}`
                : session.title
          }
          canReleaseOfficial={!!access?.can_release_official}
          sessionOwnerUserId={session.created_by_user_id}
          officialSourceOptions={officialSourceOptions}
          onClose={() => setShareOpen(false)}
          onSaved={load}
        />
      ) : (
        <ShareSessionDialog
          open={shareOpen}
          sessionId={id!}
          shares={shares}
          ownerUserId={session.created_by_user_id}
          canReleaseOfficial={!!access?.can_release_official}
          onClose={() => setShareOpen(false)}
          onUpdated={load}
        />
      )}

      <ReviewProMode
        open={proModeOpen}
        session={session}
        events={proModeEvents}
        responses={isViewerOnly ? [] : responses}
        canMarkOfficial={!!access?.can_mark_official}
        canMarkParticipant={!!access?.can_mark_participant}
        canEditAllEvents={canEditAllEvents}
        participantDeadlinePassed={participantDeadlinePassed}
        canViewOfficialMarks={canViewOfficialMarks}
        showConsensusView={showTimelineConsensusView}
        integrateOfficialMarks={integrateOfficialMarks}
        onIntegrateOfficialMarksChange={setIntegrateOfficialMarks}
        consensusViewReadOnly={consensusTimelineReadOnly}
        officialVsAllView={false}
        canOpenEventStats={canOpenEventStats}
        canUseConsensusTimeline={canUseConsensusTimeline}
        consensusTimeline={consensusTimeline}
        onConsensusTimelineChange={setConsensusTimeline}
        currentUserId={user?.id}
        officialUserId={officialUserId}
        officialUserIds={officialUserIds}
        isOfficialSourceUser={isOfficialSourceUser}
        officialSourceDisplayName={officialSourceDisplayName}
        timelineScopeSuffix={timelineScopeSuffix}
        showOfficialSourceNotice={showOfficialSourceNotice && showFacilitatorConsensusTimeline}
        showFacilitatorConsensusTimeline={showFacilitatorConsensusTimeline}
        isComparisonMode={isComparisonMode}
        initialTime={currentTime}
        initialPlaybackRate={playbackRate}
        onClose={(resumeTime, rate) => {
          if (resumeTime != null) setCurrentTime(resumeTime);
          if (rate != null) setPlaybackRate(rate);
          setProModeOpen(false);
        }}
        onMarkOfficial={addOfficialMark}
        onMarkParticipant={addParticipantMark}
        onRespond={submitResponse}
        onEditEvent={updateEvent}
        onDeleteEvent={handleDeleteEventById}
        educationMode={educationMode}
        canManageEducationMode={canManageEducationMode}
        onEducationModeChange={setEducationMode}
        decisionFilters={decisionFilters}
        onDecisionFiltersChange={setDecisionFilters}
        filterOptions={filterOptions}
      />
    </Box>
  );
}

